/**
 * The allowlist behind the login form's `redirect` parameter.
 *
 * This is a security control, not a convenience. The parameter is attacker
 * controllable end to end: anyone can send a customer a link to our own login
 * page carrying whatever `redirect` they like, and the customer sees our
 * domain, our form and our certificate before the jump. Without an allowlist
 * the login page is an open redirector, which is a phishing primitive and a
 * standard way to launder a hostile URL past a mail filter.
 *
 * The rules are deliberately narrow rather than clever:
 *
 *   - the value must be a single string. A repeated query parameter arrives as
 *     an array, and picking one of them is how two halves of a system end up
 *     disagreeing about which value was approved;
 *   - it must be a path on this origin, starting with exactly one slash. A
 *     protocol-relative `//evil.test` is a different origin that merely looks
 *     like a path, and browsers normalise a backslash to a slash, so the
 *     backslash spellings are the same attack;
 *   - every percent-decoded form of the value must pass the same structural
 *     checks, so `/%2f%2fevil.test` cannot smuggle the shape past the first
 *     look. A value still changing after three decodes is refused outright;
 *   - the path must sit under one of the prefixes below. Anything else is not
 *     a place the login form has a reason to send anyone.
 *
 * The value returned is the caller's original string, because that is what has
 * to end up in the URL. It is returned only once every form of it has passed.
 */

import type { RouteLocationRaw } from 'vue-router'

/**
 * Paths the login form may return a customer to, after a prefix match.
 *
 * Every entry has to be a place the auth guard actually bounces off, because
 * that guard is the only thing that produces a prefixed `redirect` value. An
 * entry with no producer is not neutral: it is reachable surface on a security
 * control that nothing exercises, so nothing would notice it going wrong. A
 * test asserts the invariant against AUTHENTICATED_PATH_PREFIXES. An entry may
 * be narrower than its guarded prefix, as `/oauth/consent` is, but never wider.
 */
export const LOGIN_REDIRECT_PREFIXES = ['/oauth/consent', '/dashboard'] as const

/**
 * Targets accepted only as the whole value. The invite page sends a logged-out
 * invitee to log in and back to `/invite`, where the token waits in the stash;
 * nothing below that path is a place to return to.
 */
export const LOGIN_REDIRECT_EXACT = ['/invite'] as const

/** Longer than any target we generate, short enough not to be a payload. */
const MAX_REDIRECT_LENGTH = 512

/** How many percent-decodes a legitimate target could conceivably survive. */
const DECODE_ROUNDS = 3

/**
 * The RFC 3986 characters a URL reference may legally carry, applied to the
 * raw parameter. Anything outside it (a control character, a raw space, a
 * backslash, a non-ASCII byte that should have been percent-encoded) is a
 * value that means one thing to us and another to the address bar.
 */
const URL_REFERENCE_CHARS = /^[A-Za-z0-9\-._~!$&'()*+,;=:@/?#%[\]]+$/

/**
 * Whether a decoded form carries a character a URL cannot survive: browsers
 * strip tabs and newlines before parsing, so a target containing one is read
 * differently by us and by the browser that follows it.
 */
const hasUnsafeChar = (form: string): boolean => {
  for (const ch of form) {
    const code = ch.codePointAt(0) ?? 0
    if (code <= 0x20 || (code >= 0x7f && code <= 0x9f) || code === 0xfeff) return true
  }
  return false
}

/**
 * Every percent-decoded form of `value`, including `value` itself.
 *
 * Returns null for a malformed escape sequence (decodeURIComponent throws) and
 * for a value still decoding to something new after DECODE_ROUNDS: at that
 * point the layering is the message, whatever the payload turns out to be.
 */
const decodedForms = (value: string): string[] | null => {
  let forms: string[] = [value]
  let current = value

  for (let round = 0; round < DECODE_ROUNDS; round += 1) {
    let next: string
    try {
      next = decodeURIComponent(current)
    } catch {
      return null
    }
    if (next === current) return forms
    forms = [...forms, next]
    current = next
  }

  return null
}

/** The path portion, with any query string or fragment removed. */
const pathOf = (form: string): string => form.split('#')[0].split('?')[0]

/** Whether one form of the value is a same-origin path under an allowed prefix. */
const isAllowedForm = (form: string): boolean => {
  if (!form.startsWith('/')) return false
  // A protocol-relative URL, in either spelling. Browsers treat a backslash
  // here exactly like a slash, so both are the same jump to another origin.
  if (form.startsWith('//') || form.startsWith('/\\')) return false
  if (form.includes('\\')) return false
  if (hasUnsafeChar(form)) return false

  const path = pathOf(form)
  // No traversal. The prefix match below is the whole gate, and `..` is how a
  // path that starts inside it ends up somewhere else.
  if (path.split('/').includes('..')) return false

  return LOGIN_REDIRECT_PREFIXES.some((prefix) => path === prefix || path.startsWith(`${prefix}/`))
}

/**
 * Validates a `redirect` query parameter and returns it, or null when it is
 * anything other than an allowed same-origin path.
 *
 * Callers must treat null as "send them to the default landing place", never
 * as "use the raw value".
 */
export const safeLoginRedirect = (raw: unknown): string | null => {
  if (typeof raw !== 'string') return null
  if ((LOGIN_REDIRECT_EXACT as readonly string[]).includes(raw)) return raw
  if (raw.length === 0 || raw.length > MAX_REDIRECT_LENGTH) return null
  if (!URL_REFERENCE_CHARS.test(raw)) return null

  const forms = decodedForms(raw)
  if (!forms) return null
  if (!forms.every(isAllowedForm)) return null

  return raw
}

/**
 * The login location for a customer bounced off `fullPath`, carrying where
 * they were.
 *
 * One definition for the router guard and the 401 interceptor both: the
 * validation and the shape of that location must not drift between the two
 * places that build it.
 */
export const loginRouteWithRedirect = (fullPath: string): RouteLocationRaw => {
  const redirect = safeLoginRedirect(fullPath)
  return redirect ? { name: 'login', query: { redirect } } : { name: 'login' }
}
